import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOffice2Icon,
  UsersIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

export default function TenantManagement() {
  const { token, user: currentUser } = useContext(AppContext);
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    customDomain: '',
    plan: 'standard',
    isActive: true,
    adminEmail: '',
    adminPassword: '',
    adminFirstName: '',
    adminLastName: ''
  });

  useEffect(() => {
    if (currentUser && currentUser.role !== 'super_admin') {
      navigate('/', { replace: true });
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (token && currentUser?.role === 'super_admin') {
      fetchTenants();
    }
  }, [token, currentUser]);

  if (!currentUser || currentUser.role !== 'super_admin') {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="mt-2 text-gray-600">Super admin access is required to manage tenants.</p>
      </div>
    );
  }

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/tenants', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      } else {
        setError('Failed to fetch tenants');
      }
    } catch (err) {
      setError('Error fetching tenants: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantDetail = async (id) => {
    try {
      const response = await fetch(`/api/tenants/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenantDetail(data);
        setIsDetailOpen(true);
      }
    } catch (err) {
      setError('Error fetching tenant details: ' + err.message);
    }
  };

  const handleOpenForm = (tenant = null) => {
    setError('');
    setSuccessMessage('');
    if (tenant) {
      setSelectedTenant(tenant);
      setFormData({
        name: tenant.name,
        slug: tenant.slug,
        customDomain: tenant.customDomain || '',
        plan: tenant.plan || 'standard',
        isActive: tenant.isActive,
        adminEmail: '',
        adminPassword: '',
        adminFirstName: '',
        adminLastName: ''
      });
    } else {
      setSelectedTenant(null);
      setFormData({
        name: '',
        slug: '',
        customDomain: '',
        plan: 'standard',
        isActive: true,
        adminEmail: '',
        adminPassword: '',
        adminFirstName: '',
        adminLastName: ''
      });
    }
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedTenant(null);
    setError('');
  };

  const handleSlugFromName = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const url = selectedTenant
        ? `/api/tenants/${selectedTenant.id}`
        : '/api/tenants';
      const method = selectedTenant ? 'PUT' : 'POST';

      const payload = selectedTenant
        ? {
            name: formData.name,
            slug: formData.slug,
            customDomain: formData.customDomain || null,
            plan: formData.plan,
            isActive: formData.isActive
          }
        : {
            name: formData.name,
            slug: formData.slug,
            customDomain: formData.customDomain || null,
            plan: formData.plan,
            adminEmail: formData.adminEmail,
            adminPassword: formData.adminPassword,
            adminFirstName: formData.adminFirstName,
            adminLastName: formData.adminLastName
          };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(
          selectedTenant
            ? `Tenant "${formData.name}" updated successfully.`
            : `Tenant "${formData.name}" created successfully. Admin user: ${data.adminUser?.email}`
        );
        handleCloseForm();
        fetchTenants();
      } else {
        setError(data.message || 'Error saving tenant');
      }
    } catch (err) {
      setError('Error saving tenant: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (tenant) => {
    if (tenant.isDefault) return;
    if (!window.confirm(`Set "${tenant.name}" as the default tenant? The base domain will serve this tenant's site.`)) return;
    try {
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isDefault: true })
      });
      if (response.ok) {
        setSuccessMessage(`"${tenant.name}" is now the default tenant.`);
        fetchTenants();
      } else {
        const data = await response.json();
        setError(data.message || 'Error setting default tenant');
      }
    } catch (err) {
      setError('Error setting default tenant: ' + err.message);
    }
  };

  const handleDelete = async (tenant, forceDelete = false) => {
    if (tenant.isDefault) {
      setError('Cannot delete the default tenant. Set another tenant as default first.');
      return;
    }
    
    const confirmMessage = forceDelete
      ? `⚠️ FORCE DELETE: This will permanently delete the tenant "${tenant.name}" AND ALL its data including ${tenant.userCount || 'all'} user(s), plots, contacts, sales, and everything else. This cannot be undone!\n\nType "DELETE" to confirm.`
      : `Are you sure you want to delete the tenant "${tenant.name}"? This cannot be undone.`;
    
    if (forceDelete) {
      const typed = window.prompt(confirmMessage);
      if (typed !== 'DELETE') {
        if (typed !== null) {
          setError('Force delete cancelled - you must type "DELETE" to confirm.');
        }
        return;
      }
    } else {
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      const url = forceDelete 
        ? `/api/tenants/${tenant.id}?force=true` 
        : `/api/tenants/${tenant.id}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message || `Tenant "${tenant.name}" deleted successfully.`);
        fetchTenants();
      } else {
        // If deletion failed due to users and force wasn't used, offer force delete
        if (data.userCount && !forceDelete) {
          const shouldForce = window.confirm(
            `${data.message}\n\nWould you like to force delete this tenant and ALL its data?`
          );
          if (shouldForce) {
            handleDelete(tenant, true);
          }
        } else {
          setError(data.message || 'Error deleting tenant');
        }
      }
    } catch (err) {
      setError('Error deleting tenant: ' + err.message);
    }
  };

  const handleToggleActive = async (tenant) => {
    try {
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !tenant.isActive })
      });

      if (response.ok) {
        setSuccessMessage(`Tenant "${tenant.name}" ${tenant.isActive ? 'deactivated' : 'activated'}.`);
        fetchTenants();
      } else {
        const data = await response.json();
        setError(data.message || 'Error updating tenant');
      }
    } catch (err) {
      setError('Error updating tenant: ' + err.message);
    }
  };

  const baseDomain = window.location.hostname.includes('.')
    ? window.location.hostname.split('.').slice(-2).join('.')
    : window.location.hostname;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage all tenant sites. Each tenant has its own users, data, and subdomain.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => handleOpenForm()}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
            New Tenant
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mt-4 rounded-md bg-green-50 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="mt-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Tenant
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Subdomain
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Plan
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Users
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {tenants.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-sm text-gray-500">
                          No tenants found. Create one to get started.
                        </td>
                      </tr>
                    ) : (
                      tenants.map((tenant) => (
                        <tr key={tenant.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                                  <BuildingOffice2Icon className="h-5 w-5 text-indigo-600" />
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="font-medium text-gray-900">{tenant.name}</div>
                                {tenant.customDomain && (
                                  <div className="text-gray-500 flex items-center gap-1">
                                    <GlobeAltIcon className="h-3 w-3" />
                                    {tenant.customDomain}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono">
                                {`${tenant.slug}.${baseDomain}`}
                              </code>
                              {tenant.isDefault && (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                  Default
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <button
                              onClick={() => handleToggleActive(tenant)}
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer ${
                                tenant.isActive
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                            >
                              {tenant.isActive ? (
                                <><CheckCircleIcon className="mr-1 h-3.5 w-3.5" /> Active</>
                              ) : (
                                <><XCircleIcon className="mr-1 h-3.5 w-3.5" /> Inactive</>
                              )}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-700">
                              {tenant.plan || 'standard'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="inline-flex items-center gap-1">
                              <UsersIcon className="h-4 w-4" />
                              {tenant.userCount || 0}
                            </span>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => fetchTenantDetail(tenant.id)}
                                className="text-gray-600 hover:text-gray-900"
                                title="View details"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleOpenForm(tenant)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Edit tenant"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              {!tenant.isDefault && (
                                <>
                                  <button
                                    onClick={() => handleSetDefault(tenant)}
                                    className="text-amber-600 hover:text-amber-900"
                                    title="Set as default tenant"
                                  >
                                    <CheckCircleIcon className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(tenant)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete tenant"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseForm} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                {selectedTenant ? 'Edit Tenant' : 'Create New Tenant'}
              </h3>

              {error && (
                <div className="mt-3 rounded-md bg-red-50 p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSave} className="mt-4 space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Tenant Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setFormData({
                        ...formData,
                        name,
                        ...(selectedTenant ? {} : { slug: handleSlugFromName(name) })
                      });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="St. Mary's Parish"
                  />
                </div>

                <div>
                  <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                    Subdomain Slug *
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      id="slug"
                      required
                      pattern="[a-z0-9-]+"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className="block w-full rounded-l-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="st-marys"
                    />
                    <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
                      .{baseDomain}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Lowercase letters, numbers, and hyphens only.</p>
                </div>

                <div>
                  <label htmlFor="customDomain" className="block text-sm font-medium text-gray-700">
                    Custom Domain (optional)
                  </label>
                  <input
                    type="text"
                    id="customDomain"
                    value={formData.customDomain}
                    onChange={(e) => setFormData({ ...formData, customDomain: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="cemetery.stmarys.org"
                  />
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label htmlFor="plan" className="block text-sm font-medium text-gray-700">
                      Plan
                    </label>
                    <select
                      id="plan"
                      value={formData.plan}
                      onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  {selectedTenant && (
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-2">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Active</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Admin user fields - only for new tenants */}
                {!selectedTenant && (
                  <>
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <h4 className="text-sm font-medium text-gray-900">Tenant Admin User</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        This admin user will manage this tenant's site.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="adminFirstName" className="block text-sm font-medium text-gray-700">
                          First Name
                        </label>
                        <input
                          type="text"
                          id="adminFirstName"
                          value={formData.adminFirstName}
                          onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="adminLastName" className="block text-sm font-medium text-gray-700">
                          Last Name
                        </label>
                        <input
                          type="text"
                          id="adminLastName"
                          value={formData.adminLastName}
                          onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="adminEmail"
                        required
                        value={formData.adminEmail}
                        onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="adminPassword" className="block text-sm font-medium text-gray-700">
                        Password *
                      </label>
                      <input
                        type="password"
                        id="adminPassword"
                        required
                        minLength={6}
                        value={formData.adminPassword}
                        onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </>
                )}

                <div className="mt-5 sm:mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : (selectedTenant ? 'Update Tenant' : 'Create Tenant')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {isDetailOpen && tenantDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsDetailOpen(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                {tenantDetail.tenant.name}
              </h3>
              <div className="mt-2 flex gap-2">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                  tenantDetail.tenant.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {tenantDetail.tenant.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                  {tenantDetail.tenant.plan || 'standard'}
                </span>
                {tenantDetail.tenant.isDefault && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                    Default
                  </span>
                )}
                <span className="text-sm text-gray-500">
                  Slug: <code className="bg-gray-100 px-1 rounded">{tenantDetail.tenant.slug}</code>
                </span>
              </div>

              {/* Users */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                  <UsersIcon className="h-4 w-4" />
                  Users ({tenantDetail.users.length})
                </h4>
                {tenantDetail.users.length > 0 ? (
                  <div className="mt-2 overflow-hidden rounded-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {tenantDetail.users.map((u) => (
                          <tr key={u.id}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">{u.email}</td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {u.role}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {u.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">No users in this tenant.</p>
                )}
              </div>

              {/* Cemeteries */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                  <BuildingOffice2Icon className="h-4 w-4" />
                  Cemeteries ({tenantDetail.cemeteries.length})
                </h4>
                {tenantDetail.cemeteries.length > 0 ? (
                  <ul className="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200">
                    {tenantDetail.cemeteries.map((c) => (
                      <li key={c.id} className="px-4 py-2 text-sm text-gray-900">{c.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">No cemeteries configured yet.</p>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsDetailOpen(false)}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
